import clsx from 'clsx';
import React, { RefObject } from 'react';
import { AriaButtonProps } from '@react-aria/button';
import { HiddenSelect as AriaHiddenSelect } from '@react-aria/select';
import { isAndroid, isIPhone } from '@react-aria/utils';
import { SelectState } from '@react-stately/select';
import { Node } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/select/select.module.css';
import { SelectProps } from './SelectProps';
import { AriaLabelingPropsKeys } from '../../utils/types';

export interface SelectMobileProps<
  T,
  M extends 'single' | 'multiple' = 'single'
> extends Omit<
      SelectProps<T, M>,
      | 'children'
      | 'onSelectionChange'
      | 'value'
      | 'defaultValue'
      | 'onChange'
      | 'selectionMode'
    >,
    Pick<AriaButtonProps<'button'>, AriaLabelingPropsKeys> {
  state: SelectState<T, M>;
  triggerRef: RefObject<HTMLButtonElement | HTMLDivElement>;
}

/**
 * Similar to @react-aria/select's HiddenSelect, with an extra feature of
 * triggering native select popup on mobile devices.
 *
 * IMPORTANT: make sure to render this component only inside elements with
 * "position: relative". The native select is absolutely positioned and
 * invisible and is meant to stretch the whole select button on mobile to
 * capture clicks. If the parent is not positioned relative, this might break
 * the UX on mobile.
 */
export const HiddenSelect = <
  T extends object,
  M extends 'single' | 'multiple' = 'single'
>(
  props: Omit<SelectMobileProps<T, M>, 'size'>
): JSX.Element => {
  const { state, name, isDisabled, triggerRef } = props;
  const isMobile = isAndroid() || isIPhone();

  // Notice the empty <option />.
  // We need that for mobile devices, otherwise the first option gets selected
  // by default and selecting it again won't trigger "onChange".
  if (isMobile) {
    return (
      <select
        name={name}
        className={clsx(styles['sapphire-select__native-select'])}
        disabled={isDisabled}
        onChange={(e) => {
          state.setValue(
            e.currentTarget.selectedOptions[0].getAttribute('data-key') || ''
          );
        }}
        aria-labelledby={props['aria-labelledby']}
        aria-describedby={props['aria-describedby']}
      >
        <option selected disabled hidden>
          {props.placeholder}
        </option>
        {Array.from(state.collection).map((item) =>
          item.type === 'section' ? (
            <OptGroup key={item.key} section={item} state={state}></OptGroup>
          ) : (
            <Option key={item.key} item={item} state={state}></Option>
          )
        )}
      </select>
    );
  } else {
    return (
      <AriaHiddenSelect
        isDisabled={isDisabled}
        state={state}
        triggerRef={triggerRef}
        label={props.label}
        name={props.name}
        autoComplete={props.autoComplete}
      />
    );
  }
};

const Option = <T, M extends 'single' | 'multiple' = 'single'>({
  item,
  state,
}: {
  item: Node<T>;
  state: SelectState<T, M>;
}) => (
  <option
    key={item.key}
    data-key={item.key}
    disabled={
      state.disabledKeys
        ? Array.from(state.disabledKeys).includes(item.key)
        : false
    }
    {...(state.selectedKey === item.key ? { selected: true } : {})}
  >
    {item.textValue || item.rendered}
  </option>
);

const OptGroup = <T, M extends 'single' | 'multiple' = 'single'>({
  section,
  state,
}: {
  section: Node<T>;
  state: SelectState<T, M>;
}) => (
  <optgroup key={section.key} label={section['aria-label']}>
    {[...section.childNodes].map((node) => (
      <Option key={node.key} item={node} state={state} />
    ))}
  </optgroup>
);
